import { useMemo } from "react";

interface CompanyHeaderProps {
  company: any;
  nsePrice?: number | null;
  currentPrice?: number | null;
  nsePreviousClose?: number | null;
  nseLoading?: boolean;
  trDate?: string | null;
}

const CompanyHeader = ({
  company,
  nsePrice,
  currentPrice,
  nsePreviousClose,
  nseLoading = false,
  trDate,
}: CompanyHeaderProps) => {
  // Use NSE price if available, otherwise fall back to company.price
  const displayPrice = nsePrice ?? currentPrice ?? company.price ?? 0;

  // Calculate absolute change and percentage change based on Yesterday's Close (Groww format)
  // Formula: Change = Current Price - Yesterday's Close
  //         % Change = (Change / Yesterday's Close) × 100
  const { absoluteChange, percentageChange } = useMemo(() => {
    if (nsePrice && nsePreviousClose) {
      // Step 1: Calculate absolute change from yesterday's close
      const absChange = nsePrice - nsePreviousClose;
      // Step 2: Calculate percentage change
      const percentChange = (absChange / nsePreviousClose) * 100;
      return {
        absoluteChange: absChange,
        percentageChange: percentChange,
      };
    }
    // Fallback: Calculate from company data if available
    // Previous Close = Current Price / (1 + Change%)
    if (company.price && company.change) {
      const prevClose = company.price / (1 + company.change / 100);
      const absChange = company.price - prevClose;
      const percentChange = company.change;
      return {
        absoluteChange: absChange,
        percentageChange: percentChange,
      };
    }
    // Final fallback
    const fallbackChange = company.change ?? 0;
    const absChange = company.price
      ? (company.price * fallbackChange) / 100
      : 0;
    return {
      absoluteChange: absChange,
      percentageChange: fallbackChange,
    };
  }, [nsePrice, nsePreviousClose, company.price, company.change]);

  // Determine if price is up or down
  const isPositive = absoluteChange >= 0;
  return (
    <div className="z-40 border-b border-gray-100 bg-white pb-2 pt-6">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col gap-6">
          {/* Top Row: Name and Price */}
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="font-sans text-2xl font-bold text-gray-900">
                  {company.name}
                </h1>
                <span className="rounded bg-gray-100 px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                  {company.sector}
                </span>
              </div>
              <h2 className="font-sans text-sm font-medium text-gray-500">
                {company.shortName}
              </h2>
            </div>
            <div className="flex flex-col items-end gap-1">
              {nseLoading && !nsePrice ? (
                <span className="font-sans text-3xl font-bold text-gray-400">
                  Loading...
                </span>
              ) : (
                <>
                  {/* Price Row */}
                  <span className="font-sans text-3xl font-bold text-gray-900">
                    ₹
                    {displayPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </span>

                  {/* Change Row */}
                  {displayPrice > 0 && absoluteChange !== 0 && (
                    <div className="flex items-center gap-1">
                      <span
                        className={`font-sans text-sm font-medium ${
                          isPositive ? "text-[#00D09C]" : "text-[#EB5B3C]"
                        }`}
                      >
                        {isPositive ? "+" : ""}
                        {absoluteChange.toFixed(2)} ({isPositive ? "+" : ""}
                        {percentageChange.toFixed(2)}%)
                      </span>
                      {displayPrice > 0 && (
                        <span className="font-sans text-xs font-medium text-gray-500">
                          1D
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Timestamp Row */}
              <span className="text-black-400 font-sans text-[11px] font-medium">
                As of{" "}
                {trDate
                  ? new Date(trDate).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : company.tr_date
                    ? new Date(company.tr_date).toLocaleString()
                    : new Date().toLocaleString()}
              </span>
            </div>
          </div>

          {/* Bottom Row: Detailed Meta Data */}
          <div className="grid grid-cols-2 gap-4 border-t border-gray-50 py-4 text-sm md:grid-cols-4">
            <div>
              <p className="mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                BSE Code
              </p>
              <p className="font-sans font-medium text-gray-700">
                {company.bse_code ?? company.bsecode ?? "N/A"}
              </p>
            </div>
            <div>
              <p className="mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                NSE Script
              </p>
              <p className="font-sans font-medium text-gray-700">
                {company.nse_symbol ??
                  company.nsesymbol ??
                  company.ticker ??
                  "N/A"}
              </p>
            </div>
            <div>
              <p className="mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Industry Code
              </p>
              <p className="font-sans font-medium text-gray-700">
                {company.industrycode ?? "N/A"}
              </p>
            </div>
            <div>
              <p className="mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Industry Name
              </p>
              <p className="font-sans font-medium text-gray-700">
                {company.industryname ?? company.sectorname ?? "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyHeader;
